import React from 'react';
import { useTranslation } from 'react-i18next';
import { Flag } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from 'components/ui/dialog';
import { cn } from 'lib/utils';
import { formatClock } from '../../constants';
import type { SectionReview } from './sectionReview';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sectionTitle: string;
  review: SectionReview;
  /** Seconds left in the section, or null when untimed. */
  remaining: number | null;
  advanceLabel: string;
  busy: boolean;
  onConfirm: () => void;
  /** Questions the candidate marked for review. */
  flagged: ReadonlySet<string>;
  /** Closes the review and opens that question in the section. */
  onJump: (questionId: string) => void;
}

const CHIP =
  'inline-flex min-h-11 items-center gap-1.5 rounded-full border border-[#D5DAE8] bg-white px-3.5 text-[13px] font-bold text-[#10182B] transition hover:bg-[#F4F6FB] focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#10182B] disabled:opacity-40';

const GROUP_KEY = {
  listening: 'exam.review.group_part',
  reading: 'exam.review.group_passage',
  writing: 'exam.review.group_task',
  questions: 'exam.review.group_questions',
} as const;

/** The screen between a section and the next: what is answered, what is not, and the way on or back. */
export function SectionReviewDialog({ open, onOpenChange, sectionTitle, review, remaining, advanceLabel, busy, onConfirm, flagged, onJump }: Props) {
  const { t } = useTranslation();
  const total = review.answered + review.unanswered;
  const all = review.groups.flatMap((g) => g.items);
  const flaggedItems = all.filter((i) => flagged.has(i.id));
  const unansweredItems = all.filter((i) => !i.answered);

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent
        className="flex max-h-[calc(100dvh-1rem)] max-w-[calc(100%-1.5rem)] flex-col gap-0 overflow-hidden rounded-[22px] p-0 max-sm:bottom-0 max-sm:left-0 max-sm:top-auto max-sm:max-h-[calc(100dvh-2.5rem)] max-sm:w-full max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-b-none sm:max-h-[92dvh] sm:max-w-[960px] sm:rounded-[26px]"
        closeClassName="hidden"
      >
        <div className="shrink-0 px-4 pb-3 pt-5 sm:px-8 sm:pt-8">
          <div className="flex items-start justify-between gap-4 sm:gap-5">
            <div className="min-w-0">
              <span className="font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-[#B42318]">{t('exam.review.eyebrow')}</span>
              <DialogTitle className="mt-1.5 font-display text-xl font-extrabold leading-tight tracking-[-0.04em] text-[#10182B] sm:mt-2 sm:text-[28px]">
                {t('exam.review.title', { section: sectionTitle })}
              </DialogTitle>
            </div>
            {remaining !== null && (
              <div className="shrink-0 text-right">
                <div className="font-mono text-xl font-bold tabular-nums text-[#10182B] sm:text-[26px]">{formatClock(remaining)}</div>
                <div className="text-xs text-[#646D84]">{t('exam.review.time_left')}</div>
              </div>
            )}
          </div>
          <DialogDescription className="sr-only">{t('exam.confirm.no_return')}</DialogDescription>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4 sm:px-8">
          <div className="grid grid-cols-3 gap-2 sm:flex sm:flex-wrap">
            <Summary value={review.answered} label={t('exam.review.answered')} />
            <Summary value={review.unanswered} label={t('exam.review.unanswered')} warn={review.unanswered > 0} />
            <Summary value={flaggedItems.length} label={t('exam.review.flagged')} />
          </div>

          {(flaggedItems.length > 0 || unansweredItems.length > 0) && (
            <div className="mt-3 flex flex-wrap gap-2">
              {flaggedItems.length > 0 && (
                <button type="button" onClick={() => onJump(flaggedItems[0].id)} disabled={busy} className={CHIP}>
                  <Flag className="h-3.5 w-3.5 text-[#B42318]" aria-hidden />
                  {t('exam.review.go_flagged', { count: flaggedItems.length })}
                </button>
              )}
              {unansweredItems.length > 0 && (
                <button type="button" onClick={() => onJump(unansweredItems[0].id)} disabled={busy} className={CHIP}>
                  {t('exam.review.go_unanswered', {
                    count: unansweredItems.length,
                  })}
                </button>
              )}
            </div>
          )}

          {review.groups.map((group) => {
            const first = group.items[0].number;
            const last = group.items[group.items.length - 1].number;
            return (
              <div key={group.key} className="mt-5">
                <h3 className="mb-2.5 text-[13px] font-bold text-[#4B5468]">
                  {t(GROUP_KEY[group.kind], {
                    n: group.index,
                    from: first,
                    to: last,
                  })}
                </h3>
                <ul className="flex flex-wrap gap-[7px]">
                  {group.items.map((q) => (
                    <li key={q.id}>
                      <button
                        type="button"
                        onClick={() => onJump(q.id)}
                        aria-label={`${t('exam.nav.question_status', {
                          n: q.number,
                          status: q.answered ? t('exam.nav.answered') : t('exam.nav.unanswered'),
                        })}${flagged.has(q.id) ? `, ${t('exam.flag.flagged')}` : ''}`}
                        title={t('exam.review.go_to_question', { n: q.number })}
                        className={cn(
                          'relative flex h-11 w-11 items-center justify-center rounded-[10px] border-[1.5px] text-[13.5px] font-bold transition hover:brightness-95 sm:h-[38px] sm:w-[38px] focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#10182B]',
                          q.answered ? 'border-[#10182B] bg-[#10182B] text-white' : 'border-[#B42318] bg-[#FDECEC] text-[#B42318]',
                          flagged.has(q.id) &&
                            "after:absolute after:right-[-1px] after:top-[-1px] after:h-0 after:w-0 after:rounded-tr-[9px] after:border-l-[11px] after:border-t-[11px] after:border-l-transparent after:border-t-[#B42318] after:content-['']",
                        )}
                      >
                        {q.number}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}

          {total > 0 && (
            <div className="mt-5 flex flex-wrap items-center gap-x-[18px] gap-y-1.5 text-[12.5px] text-[#4B5468]">
              <Legend swatch="border-[#10182B] bg-[#10182B]" label={t('exam.nav.answered')} />
              <Legend swatch="border-[#B42318] bg-[#FDECEC]" label={t('exam.nav.unanswered')} />
              <span className="flex items-center gap-1.5">
                <i className="inline-block h-0 w-0 border-l-[7px] border-t-[7px] border-l-transparent border-t-[#B42318]" aria-hidden />
                {t('exam.flag.flagged')}
              </span>
            </div>
          )}

          <p
            className={cn(
              'mt-5 rounded-xl px-4 py-3 text-[13.5px] font-semibold leading-relaxed',
              review.unanswered > 0 ? 'bg-[#FFF4D6] text-[#92400E]' : 'bg-[#F4F6FB] text-[#364055]',
            )}
          >
            {review.unanswered > 0 && <>{t('exam.confirm.unanswered', { count: review.unanswered })}. </>}
            {t('exam.confirm.no_return')}
          </p>
        </div>

        <div className="flex shrink-0 gap-2.5 max-[359px]:flex-col-reverse border-t border-[#E6E9F2] bg-white px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 sm:justify-end sm:px-8 sm:pb-6 sm:pt-4">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="inline-flex min-h-12 shrink-0 items-center justify-center whitespace-nowrap rounded-xl border border-[#D5DAE8] bg-white px-4 text-sm font-bold text-[#10182B] transition hover:bg-[#F4F6FB] focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#10182B] disabled:opacity-40 sm:min-h-11 sm:px-[18px]"
          >
            {t('exam.review.back')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="inline-flex min-h-12 min-w-0 flex-1 items-center justify-center gap-2 rounded-xl bg-[#10182B] px-4 text-sm font-bold text-white transition hover:brightness-95 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#10182B] disabled:opacity-40 sm:min-h-11 sm:flex-none"
          >
            <Flag className="h-4 w-4 shrink-0" aria-hidden />
            <span className="leading-tight sm:truncate">{advanceLabel}</span>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Summary({ value, label, warn = false }: { value: number; label: string; warn?: boolean }) {
  return (
    <div
      className={cn(
        'flex flex-col items-start justify-center rounded-[14px] border px-3 py-2 text-xs font-semibold leading-tight sm:flex-row sm:items-center sm:gap-2.5 sm:px-4 sm:py-2.5 sm:text-[13.5px]',
        warn ? 'border-[#F5B5B0] bg-[#FDECEC] text-[#B42318]' : 'border-[#E6E9F2] text-[#10182B]',
      )}
    >
      <b className="font-display text-2xl tracking-[-0.03em]">{value}</b>
      {label}
    </div>
  );
}

function Legend({ swatch, label }: { swatch: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <i className={cn('inline-block h-3.5 w-3.5 rounded border-[1.5px]', swatch)} aria-hidden />
      {label}
    </span>
  );
}

export default SectionReviewDialog;
